// @ts-nocheck
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { extractStylesToArray, isSafe } from "@insite/client-framework/Common/Utilities/isSafeStyles";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { injectCss } from "@insite/mobius/utilities";
import { BreakpointKey } from "@insite/mobius/utilities/breakpointMediaQueries";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import useIsBreakpoint from "@toolkit/Framework/Common/Hooks/useIsBreakpoint";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import * as cssLinter from "css";
import parse from "html-react-parser";
import * as React from "react";
import styled, { css } from "styled-components";

const enum fields {
    backgroundColor = "backgroundColor",
    textColor = "textColor",
    padding = "padding",
    mobilePadding = "mobilePadding",
    content = "content",
    mobileContent = "mobileContent",
    customCSS = "customCSS",
}

interface Props extends WidgetProps {
    fields: {
        [fields.backgroundColor]: string;
        [fields.textColor]: string;
        [fields.padding]: number;
        [fields.mobilePadding]: number;
        [fields.content]: string;
        [fields.mobileContent]: string;
        [fields.customCSS]: string;
    };
}

const mobileBreakpoint: BreakpointKey = "xs";

const NewsBanner: React.FunctionComponent<Props> = props => {
    const {
        fields: { backgroundColor, textColor, padding, mobilePadding, content, mobileContent, customCSS },
    } = props;

    const isMobile = useIsBreakpoint(mobileBreakpoint, "down");

    const customCssWrapper = React.useMemo(
        () => ({
            css: css`
                color: ${getColor("primary.contrast")};
                background: ${getColor("primary.main")};
                text-align: center;
                font-size: ${pxToRem(16)};
                ${customCSS}
            `,
        }),
        [customCSS],
    );

    const parsedResponsiveContent = React.useMemo(() => {
        const html = isMobile && mobileContent ? mobileContent : content;
        if (!html) {
            return null;
        }
        return parse(html, parserOptions);
    }, [isMobile, content, mobileContent]);

    const effectivePadding = React.useMemo(
        () => (isMobile && typeof mobilePadding === "number" && !Number.isNaN(mobilePadding) ? mobilePadding : padding),
        [isMobile, padding, mobilePadding],
    );

    if (!content) {
        return null;
    }

    return (
        <StyledWrapper>
            <StyledWrapper {...customCssWrapper}>
                <ContentWrapper
                    className="wrapper"
                    backgroundColor={backgroundColor}
                    textColor={textColor}
                    padding={effectivePadding}
                >
                    {parsedResponsiveContent}
                </ContentWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const defaultCustomCss = `.wrapper{
}`;

const widgetModule: WidgetModule = {
    component: NewsBanner,
    definition: {
        group: "Basic",
        icon: "text",
        fieldDefinitions: [
            {
                fieldType: "General",
                name: fields.backgroundColor,
                editorTemplate: "ColorPickerField",
                displayName: "Background Color",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.textColor,
                editorTemplate: "ColorPickerField",
                displayName: "Text Color",
                defaultValue: "",
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.padding,
                editorTemplate: "IntegerField",
                displayName: "Content Padding",
                defaultValue: 0,
                tab: basicTab,
            },
            {
                fieldType: "General",
                name: fields.mobilePadding,
                editorTemplate: "IntegerField",
                displayName: "Mobile Content Padding",
                defaultValue: 0,
                tab: basicTab,
            },
            {
                name: fields.content,
                displayName: "Content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Contextual",
                validate: (html: string) => {
                    const arrayOfStyles = extractStylesToArray(html);
                    return arrayOfStyles?.every(style => isSafe(style))
                        ? null
                        : "The HTML contains invalid styles that prevent it from being saved";
                },
                tab: basicTab,
            },
            {
                name: fields.mobileContent,
                displayName: "Mobile Content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "Contextual",
                validate: (html: string) => {
                    const arrayOfStyles = extractStylesToArray(html);
                    return arrayOfStyles?.every(style => isSafe(style))
                        ? null
                        : "The HTML contains invalid styles that prevent it from being saved";
                },
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;

export const ContentWrapper = styled.div<
    { backgroundColor: string; textColor: string; padding: number } & InjectableCss
>`
    ${({ backgroundColor }) => (backgroundColor ? `background-color: ${backgroundColor};` : null)};
    ${({ textColor }) => (textColor ? `color: ${textColor};` : null)};
    ${({ padding }) => (padding ? `padding: ${padding}px;` : "padding: 0.35em 1em;")};
    & > *:last-child {
        margin: 0;
    }
    ${injectCss}
`;
